import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from '../types';
import { supabase } from '../lib/supabase';
import {
  signIn,
  clearAllLocalCache,
  resetPassword as resetPasswordService,
  updatePassword as updatePasswordService,
} from '../services/auth';
import { auditLogger } from '../services/auditLogger';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  /** @deprecated — no-op kept for API compatibility */
  setUnlockingSession: (value: boolean) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // No-op — kept so InactivityLockModal doesn't break
  const setUnlockingSession = useCallback((_value: boolean) => {}, []);

  const fetchUserData = useCallback(async (userId: string): Promise<User | null> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, nom, prenom, role, organisation_id, statut, telephone, poste, two_factor_enabled, must_change_password, password_changed_at, password_expiry_days, password_history, derniere_connexion')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      return (data as User) || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Supabase émet INITIAL_SESSION dès l'enregistrement du listener
    // avec la session persistée dans localStorage — pas besoin d'appeler getSession() séparément.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        // SIGNED_IN est géré directement par login().
        // USER_UPDATED est géré par refreshUser() explicite après changement de mot de passe —
        // l'ignorer ici évite que le handler asynchrone écrase l'état mis à jour avec des données périmées.
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') return;

        if (!session?.user) {
          if (event === 'SIGNED_OUT') clearAllLocalCache();
          if (mounted) {
            setUser(null);
            setLoading(false);
          }
          return;
        }

        // INITIAL_SESSION ou TOKEN_REFRESHED — charger/rafraîchir le profil
        const userData = await fetchUserData(session.user.id);
        if (!mounted) return;

        // Déconnecter automatiquement les comptes suspendus ou inactifs
        if (userData && userData.statut !== 'actif') {
          await supabase.auth.signOut({ scope: 'local' });
          clearAllLocalCache();
          if (mounted) { setUser(null); setLoading(false); }
          return;
        }

        // Marquer l'utilisateur en ligne (session active détectée)
        if (userData) {
          void (async () => { try { await supabase.rpc('user_connexion'); } catch (_) {} })();
        }

        // Ne pas écraser l'état si fetchUserData échoue temporairement
        if (userData) setUser(userData);
        setLoading(false);
      }
    );

    // Le hook d'inactivité signale une déconnexion forcée via cet événement custom
    const handleForceLogout = () => {
      supabase.rpc('user_deconnexion').then(null, () => {});
      supabase.auth.signOut({ scope: 'local' }).catch(() => {
        clearAllLocalCache();
        if (mounted) setUser(null);
      });
    };
    window.addEventListener('force-logout', handleForceLogout);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      window.removeEventListener('force-logout', handleForceLogout);
    };
  }, [fetchUserData]);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const result = await signIn({ email, password });
      // Mise à jour immédiate depuis le résultat de signIn
      // (onAuthStateChange confirmera et maintiendra la session)
      if (result?.userData) {
        setUser(result.userData as User);
      } else if (result?.user?.id) {
        const userData = await fetchUserData(result.user.id);
        if (userData) setUser(userData);
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    const userId = user?.id;
    try {
      // Marquer l'utilisateur hors ligne avant d'invalider la session
      await supabase.rpc('user_deconnexion').then(null, () => {});
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) throw error;
      // Log d'audit non-bloquant (fire-and-forget)
      if (userId) {
        auditLogger.logDeconnexion(userId).catch(() => {});
      }
    } catch {
      // signOut a échoué — nettoyer manuellement l'état local
      clearAllLocalCache();
      setUser(null);
    }
    // L'événement SIGNED_OUT de onAuthStateChange vide aussi l'état
  };

  const resetPassword = async (email: string) => {
    await resetPasswordService({ email });
  };

  const updatePassword = async (newPassword: string) => {
    await updatePasswordService({ password: newPassword });
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const userData = await fetchUserData(session.user.id);
        if (userData) setUser(userData);
      }
    } catch { /* conserver l'état courant */ }
  }, [fetchUserData]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, resetPassword, updatePassword, setUnlockingSession, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const useAuthContext = useAuth;
